import { Component, input, output, computed, signal, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators, AsyncValidatorFn } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxIntlTelInputModule, SearchCountryField, CountryISO } from 'ngx-intl-tel-input';
import { ActivatedRoute } from '@angular/router';
import { Md5 } from 'md5-typescript';
import { DateTime } from 'luxon';
import { TextboxComponent } from '../textbox/textbox.component';
import { ButtonComponent } from '../button/button.component';
import { CheckboxComponent, CheckboxItem } from '../checkbox/checkbox.component';
import { FormDropdownComponent } from '../form-dropdown/form-dropdown.component';
import { StoreService } from '../../../store/store.service';
import { LoyaltyService } from '../../services/loyalty.service';
import { ModalService } from '../../services/modal.service';
import {
  STRING_REGEX,
  EMAIL_PATTERN_REGEX,
  PASSWORD_REGEX,
  PHONE_REGEX,
  STATE_REGEX,
  CVV_REGEX,
  SPACE_TAB_REGEX,
  QUERY_PARAM_ATTRIBUTES,
  checkRecaptchaConfig,
} from '../../configs/common.config';
import * as _ from 'lodash';
import { SessionManagerService } from '../../services/session-manager.service';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { RecaptchaService } from '../../services/recaptcha.service';
import { PhoneInputAccessibilityDirective } from '../../directives/phone-input-accessibility.directive';

// Form field configuration interface
export interface SignUpFormField {
  name: string;
  type: string;
  fieldName: string;
  regex?: RegExp;
  invalidErrMsg?: string;
  autoCompleteType?: string;
  show: boolean;
  compulsory: boolean;
  minDate?: NgbDate;
  maxDate?: NgbDate;
  isDropdown?: boolean;
  data?: any[];
  minLength?: number;
  maxLength?: number;
  minLengthErrMsg?: string;
  maxLengthErrMsg?: string;
  patternErrMsg?: string;
  mismatchErrMsg?: string;
  requiredErrMsg?: string;
  label?: string;
  urlLabel?: string;
  url?: string;
}

@Component({
  standalone: true,
  selector: 'app-loyalty-sign-up',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbDatepickerModule,
    NgxIntlTelInputModule,
    TextboxComponent,
    ButtonComponent,
    CheckboxComponent,
    FormDropdownComponent,
    TooltipComponent,
    PhoneInputAccessibilityDirective
  ],
  templateUrl: './loyalty-sign-up.component.html',
  styleUrl: './loyalty-sign-up.component.scss',
})
export class LoyaltySignUpComponent {
  // Services
  private readonly ngbCal = inject(NgbCalendar);

  // Input properties
  public formLayout = input<'single' | 'two-column'>('single');
  public context = input<'sign-up-page' | 'side-panel'>('sign-up-page');

  // Output events
  public handleCloseSidePanel = output<void>();
  public handleOpenSidePanel = output<string>();

  // Form
  public registerFormGroup: FormGroup = new FormGroup({});
  public registerFormFields: SignUpFormField[] = [];
  public isRegisterFormSubmitted = signal<boolean>(false);
  public showPasswordMap: { [key: string]: boolean } = {};
  public isFormInitialized = signal<boolean>(false);

  // Phone number configuration for ngx-intl-tel-input
  public phoneFormControl = new FormControl('');
  public businessPhoneFormControl = new FormControl('');
  public cellPhoneFormControl = new FormControl('');
  public SearchCountryField = SearchCountryField;
  public CountryISO = CountryISO;
  public selectedCountryISO = signal<CountryISO>(CountryISO.UnitedStates);

  // Computed values
  public propInfo = computed(() => this._storeSvc.propInfo());
  public localeObj = computed(() => this._storeSvc.localeObj());
  public isUppercaseLabels = computed(() => this._storeSvc.appStore.isUpperCaseLabel());
  public countryDataList = computed(() => this._storeSvc.countryStateData());
  public clientCountry = computed(() => this.propInfo().clientCountry);
  public mandatoryTextMsg = computed(() => {
    const msg = this.localeObj()?.tf_4_Checkout_preferences_compulsoryFields;
    return msg?.replace('*', '<span class="error-msg">*</span>');
  });

  public isMobile = computed(() => this._storeSvc.res().mobile);

  // Computed country list with localized names
  public preparedCountryList = computed(() => {
    const countryList = this.countryDataList();
    const localeObj = this.localeObj();
    const isUppercase = this.isUppercaseLabels();

    if (!countryList || countryList.length === 0) {
      return [];
    }

    return countryList.map((country: any) => {
      let name = localeObj[`tf_4_Checkout_Location_${country.countryCode_3}`] || country.name;
      if (isUppercase) {
        name = name?.toUpperCase();
      }
      return {
        ...country,
        name,
        countryCode: country.countryCode_2,
      };
    });
  });

  // Loading state
  public isSignUpInProgress = this.loyaltySvc.isSignUpInProgress;
  public isRecaptchaInProgress = computed(() => this.recaptchaSvc.isRecaptchaInProgress());

  public isRegisterButtonLoading = computed(() => {
    return this.isSignUpInProgress() || this.isRecaptchaInProgress();
  });

  constructor(
    public _storeSvc: StoreService,
    private loyaltySvc: LoyaltyService,
    private route: ActivatedRoute,
    private sessionManagerSvc: SessionManagerService,
    private modalSvc: ModalService,
    private recaptchaSvc: RecaptchaService
  ) {
    // Watch for localeObj changes and initialize form when ready
    effect(() => {
      const localeObj = this.localeObj();
      const propInfo = this.propInfo();

      // Both localeObj and propInfo with loyaltySignUpSettingsInfo
      if (localeObj && Object.keys(localeObj).length > 0
        && propInfo?.loyaltySignUpSettingsInfo && !this.isFormInitialized()) {
        this.initializeFormFields();
        this.generateFormControls();
        this.isFormInitialized.set(true);
      }
    });
    this.initializePhoneCountryFlag();
  }

  private initializePhoneCountryFlag(): void {
    effect(() => {
      const { clientCountryISO2Code } = this.propInfo() || {};
      if (clientCountryISO2Code) {
        const selectedCountry = clientCountryISO2Code.toLowerCase() as CountryISO;
        if (selectedCountry && selectedCountry !== this.selectedCountryISO()) {
          this.selectedCountryISO.set(selectedCountry);
        }
      }
    });
  }

  /**
   * Transform array data to dropdown format { key, value }
   */
  public transformToDropdownFormat(data: any[]): any[] {
    if (!data || data.length === 0) return [];

    // Filter out any undefined/null items
    const validData = data.filter((item) => item !== null);

    if (validData.length === 0) return [];

    // If already in {key, value} format
    if (validData[0]?.key !== undefined && validData[0]?.value !== undefined) {
      return validData;
    }

    // Transform to {key, value} format
    return validData.map((item: any) => {
      let key = '';
      let value = '';

      if (typeof item === 'string') {
        // Simple string array (e.g., gender)
        key = item;
        value = item;
      } else if (item?.countryCode) {
        // Country data: key = country code, value = display name
        key = item.countryCode;
        value = item.name || item.countryCode;
      } else if (item?.code) {
        // State data: key = state code, value = display name
        key = item.code;
        value = item.name || item.code;
      } else if (item?.name) {
        // Generic data with name property
        key = item.name;
        value = item.name;
      } else {
        // Fallback
        key = String(item);
        value = String(item);
      }

      return {
        key,
        value
      };
    });
  }

  /**
   * Initialize form fields configuration based on property settings
   */
  private initializeFormFields(): void {
    const localeObj = this.localeObj();
    const propInfo = this.propInfo();

    // Build register form fields array
    this.registerFormFields = [
      {
        name: localeObj.tf_4_Checkout_guestDetails_firstName,
        type: 'text',
        regex: STRING_REGEX,
        fieldName: 'first_name',
        invalidErrMsg: localeObj.tf_4_Checkout_guestDetails_invalidFirstNameError,
        autoCompleteType: 'given-name',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.first_name || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.first_name ?? false,
      },
      {
        name: localeObj.tf_4_Checkout_guestDetails_lastName,
        type: 'text',
        regex: STRING_REGEX,
        fieldName: 'last_name',
        invalidErrMsg: localeObj.tf_4_Checkout_guestDetails_invalidLastNameError,
        autoCompleteType: 'family-name',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.last_name || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.last_name ?? false,
      },
      {
        name: localeObj.tf_1_Calendar_Loyalty_signUp_DOBlabel,
        type: 'date',
        fieldName: 'date_of_birth',
        minDate: NgbDate.from({ year: 1900, month: 1, day: 1 }) || undefined,
        maxDate: this.ngbCal.getToday(),
        invalidErrMsg: localeObj.tf_4_Checkout_guestDetails_invalidDOB,
        autoCompleteType: 'date-of-birth',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.date_of_birth || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.date_of_birth ?? false,
      },
      {
        name: localeObj.tf_4_Checkout_guestDetails_emailAddress,
        type: 'email',
        regex: EMAIL_PATTERN_REGEX,
        fieldName: 'email',
        invalidErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_emailInvalidError,
        autoCompleteType: 'email',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.email || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.email ?? false,
      },
      {
        name: localeObj.tf_4_Checkout_guestDetails_phone,
        type: 'phone',
        regex: PHONE_REGEX,
        fieldName: 'phone',
        invalidErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_InvalidPhoneError,
        autoCompleteType: 'phone',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.phone || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.phone ?? false,
      },
      {
        name: localeObj.tf_1_Calendar_Loyalty_signUp_Gender,
        type: 'text',
        fieldName: 'gender',
        isDropdown: true,
        data: [
          localeObj.tf_1_Calendar_Loyalty_signUp_male,
          localeObj.tf_1_Calendar_Loyalty_signUp_female,
          localeObj.tf_1_Calendar_Loyalty_signUp_others,
        ],
        invalidErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_invalidGender,
        autoCompleteType: 'gender',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.gender || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.gender ?? false,
      },
      {
        name: localeObj.tf_1_Calendar_Loyalty_signUp_BusinessPhone,
        type: 'business-phone',
        regex: PHONE_REGEX,
        fieldName: 'business_phone',
        invalidErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_invalidBussinesPhone,
        autoCompleteType: 'businessPhone',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.business_phone || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.business_phone ?? false,
      },
      {
        name: localeObj.tf_1_Calendar_Loyalty_signUp_CellPhone,
        type: 'cell-phone',
        regex: PHONE_REGEX,
        fieldName: 'cellPhone',
        invalidErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_invalid_cellPhone,
        autoCompleteType: 'cellPhone',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.cell_phone || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.cell_phone ?? false,
      },
      {
        name: localeObj.tf_4_Checkout_guestDetails_addressLine1,
        type: 'text',
        fieldName: 'addressLine1',
        invalidErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_invalid_AddressLine1,
        autoCompleteType: 'addressLine1',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.address1 || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.address1 ?? false,
      },
      {
        name: localeObj.tf_4_Checkout_guestDetails_addressLine2,
        type: 'text',
        fieldName: 'addressLine2',
        invalidErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_invalid_AddressLine2,
        autoCompleteType: 'addressLine2',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.address2 || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.address2 ?? false,
      },
      {
        name: localeObj.tf_4_Checkout_guestDetails_city,
        type: 'text',
        regex: STATE_REGEX,
        fieldName: 'city',
        invalidErrMsg: localeObj.tf_4_Checkout_guestDetails_cityReqError,
        autoCompleteType: 'city',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.city || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.city ?? false,
      },
      {
        name: localeObj.tf_4_Checkout_guestDetails_state,
        type: 'text',
        regex: STATE_REGEX,
        fieldName: 'state',
        isDropdown: true,
        data: [],
        invalidErrMsg: localeObj.tf_4_Checkout_guestDetails_stateReqError,
        autoCompleteType: 'state',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.state || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.state ?? false,
      },
      {
        name: localeObj.tf_1_Calendar_Loyalty_ManageAccount_BillingInfo_country,
        type: 'text',
        regex: STRING_REGEX,
        fieldName: 'country',
        invalidErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_invalid_Country,
        autoCompleteType: 'country',
        isDropdown: true,
        data: [], // Will be populated reactively from preparedCountryList computed signal
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.country || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.country ?? false,
      },
      {
        name: localeObj.tf_1_Calendar_Loyalty_signUp_ZIP,
        type: 'text',
        regex: CVV_REGEX,
        fieldName: 'zip',
        invalidErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_InvalidZIP,
        autoCompleteType: 'zip',
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.zip_code || false,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.zip_code ?? false,
      },
      {
        name: localeObj.tf_1_Calendar_Loyalty_signIn_password,
        type: 'password',
        regex: propInfo.loyaltySignUpSettingsInfo?.password_policy_regex || PASSWORD_REGEX,
        fieldName: 'password',
        minLength: 8,
        maxLength: 64,
        minLengthErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_pwdMinLengthError,
        maxLengthErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_pwdMaxLengthError,
        patternErrMsg: propInfo.loyaltySignUpSettingsInfo?.sign_up_error
          || localeObj.tf_1_Calendar_Loyalty_signUp_pwdPatternError,
        autoCompleteType: 'password',
        show: propInfo.loyaltySignUpSettingsInfo?.enable_password_signup || false,
        compulsory: true,
      },
      {
        name: localeObj.tf_1_Calendar_Loyalty_signUp_confirmPassword,
        type: 'password',
        fieldName: 'confirmPassword',
        mismatchErrMsg: localeObj.tf_1_Calendar_Loyalty_signUp_passwordsMismatchError,
        autoCompleteType: 'password',
        show: propInfo.loyaltySignUpSettingsInfo?.enable_password_signup || false,
        compulsory: true,
      },
      {
        name: localeObj?.tf_1_Calendar_Loyalty_terms_conditions,
        type: 'checkbox',
        fieldName: 'agree_terms',
        requiredErrMsg: localeObj?.tf_1_Calendar_Loyalty_termsConditions_errormessage,
        label: localeObj?.tf_1_Calendar_Loyalty_termsConditions_privacyPolicy,
        urlLabel: localeObj?.tf_1_Calendar_Loyalty_terms_conditions,
        url: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.terms_conditions_path,
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.terms_conditions_path,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.terms_conditions_path,
      },
      {
        name: localeObj?.tf_1_Calendar_Loyalty_privac_policy,
        type: 'checkbox',
        fieldName: 'privacy_policy',
        requiredErrMsg: localeObj?.tf_1_Calendar_Loyalty_privacyPolicy_errormessage,
        label: localeObj?.tf_1_Calendar_Loyalty_termsConditions_privacyPolicy,
        urlLabel: localeObj?.tf_1_Calendar_Loyalty_privac_policy,
        url: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.privacy_policy_path,
        show: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_display_fields?.privacy_policy_path,
        compulsory: propInfo.loyaltySignUpSettingsInfo?.loyalty_signup_required_fields?.privacy_policy_path,
      },
    ];

    // Filter to show only fields marked as 'show'
    this.registerFormFields = this.registerFormFields.filter((field) => field.show);
  }

  /**
   * Generate form controls based on field configuration
   */
  private generateFormControls(): void {
    this.registerFormFields.forEach((field) => {
      // Skip phone fields - they are managed separately
      if (['text', 'email', 'date'].includes(field.type)) {
        this.registerFormGroup.setControl(
          field.fieldName,
          new FormControl(null, [
            field.compulsory === true ? Validators.required : Validators.nullValidator,
            Validators.pattern(field?.regex || /.*/),
          ]),
        );
      } else if (field.fieldName === 'password') {
        this.registerFormGroup.setControl(
          field.fieldName,
          new FormControl('', {
            validators: [Validators.required, Validators.pattern(field.regex || PASSWORD_REGEX)],
            asyncValidators: [this.checkPasswords.bind(this)] as AsyncValidatorFn[],
            updateOn: 'change',
          }),
        );
      } else if (field.fieldName === 'confirmPassword') {
        this.registerFormGroup.setControl(
          field.fieldName,
          new FormControl('', {
            validators: [Validators.required],
            asyncValidators: [this.checkPasswords.bind(this)] as AsyncValidatorFn[],
            updateOn: 'change',
          }),
        );
      } else if (field.type === 'checkbox') {
        this.registerFormGroup.setControl(
          field.fieldName,
          new FormControl(false, {
            validators: [Validators.required],
            asyncValidators: [this.validateCheckBox.bind(this)] as AsyncValidatorFn[],
            updateOn: 'change',
          }),
        );
      } else if (['phone', 'cell-phone', 'business-phone'].includes(field.type)) {
        this.registerFormGroup.setControl(
          field.fieldName,
          new FormControl('', field.compulsory ? [Validators.required] : []),
        );
      }
    });

    // Set validators for intl-tel-input country code controls
    const phoneField = this.registerFormFields.find((f) => f.fieldName === 'phone');
    const businessPhoneField = this.registerFormFields.find((f) => f.fieldName === 'business_phone');
    const cellPhoneField = this.registerFormFields.find((f) => f.fieldName === 'cellPhone');

    if (phoneField?.compulsory) {
      this.phoneFormControl.setValidators([Validators.required]);
    }
    if (businessPhoneField?.compulsory) {
      this.businessPhoneFormControl.setValidators([Validators.required]);
    }
    if (cellPhoneField?.compulsory) {
      this.cellPhoneFormControl.setValidators([Validators.required]);
    }

    // Initialize country field with client country code
    const clientCountryName = this.clientCountry();
    const countryList = this.countryDataList();
    const clientCountryData = countryList.find(
      (c: any) => c.name?.toUpperCase() === clientCountryName?.toUpperCase()
    );
    const clientCountryCode = clientCountryData?.countryCode_2 || '';

    this.registerFormGroup.patchValue({ country: clientCountryCode });
    this.updateStatesDataList(clientCountryCode);
  }

  /**
   * Async validator for password and confirm password match
   */
  private checkPasswords(control: FormControl): Promise<any> {
    return new Promise<any>((resolve) => {
      const password = control.parent?.get('password') as FormControl;
      const confirmPassword = control.parent?.get('confirmPassword') as FormControl;

      if (!password || !confirmPassword) {
        resolve(null);
        return;
      }

      if (password.value === confirmPassword.value) {
        // Clear mismatch errors
        if (password?.errors?.['mismatch']) {
          const passwordErrors = _.cloneDeep(password.errors);
          delete passwordErrors['mismatch'];
          if (_.isEmpty(passwordErrors)) {
            password.setErrors(null);
          } else {
            password.setErrors(passwordErrors);
          }
        }
        if (confirmPassword?.errors?.['mismatch']) {
          const confirmPasswordErrors = _.cloneDeep(confirmPassword.errors);
          delete confirmPasswordErrors['mismatch'];
          if (_.isEmpty(confirmPasswordErrors)) {
            confirmPassword.setErrors(null);
          } else {
            confirmPassword.setErrors(confirmPasswordErrors);
          }
        }
        resolve(null);
      } else {
        // Set mismatch errors
        password.setErrors({ ...password.errors, mismatch: true });
        confirmPassword.setErrors({ ...confirmPassword.errors, mismatch: true });
        resolve({ mismatch: true });
      }
    });
  }

  /**
   * Async validator for checkbox
   */
  private validateCheckBox(control: FormControl): Promise<any> {
    return new Promise<any>((resolve) => {
      if (control.value) {
        if (control?.errors?.['required']) {
          const cbErrors = _.cloneDeep(control.errors);
          delete cbErrors['required'];
          if (_.isEmpty(cbErrors)) {
            control.setErrors(null);
          } else {
            control.setErrors(cbErrors);
          }
        }
        resolve(null);
      } else {
        control.setErrors({ ...control.errors, required: true });
        resolve({ required: true });
      }
    });
  }

  /**
   * Update states data list based on selected country
   */
  private updateStatesDataList(countryCode?: string): void {
    if (this.registerFormFields) {
      const stateField = this.registerFormFields.find((f) => f.fieldName === 'state');
      const selectedCountryCode = countryCode || this.registerFormGroup.get('country')?.value;
      const countryList = this.countryDataList();

      if (stateField && selectedCountryCode) {
        const selectedCountry = countryList.find(
          (c: any) => c.countryCode_2?.toUpperCase() === selectedCountryCode?.toUpperCase() && c.hasExplicitStates,
        );

        stateField.data = selectedCountry?.states || [];
        stateField.isDropdown = !!stateField.data?.length;
      }
    }
  }

  /**
   * Toggle password visibility
   */
  public togglePasswordVisibility(fieldName: string): void {
    this.showPasswordMap[fieldName] = !this.showPasswordMap[fieldName];
  }

  /**
   * Handle country change
   */
  public onCountryChange(): void {
    this.registerFormGroup.get('state')?.patchValue('');
    this.updateStatesDataList();
  }

  /**
   * Navigate to sign-in panel - opens the sign-in panel
   */
  public handleSignInSidePanel = () => {
    // this.handleCloseSidePanel.emit();
    // Use setTimeout to ensure current panel closes before opening new one
    setTimeout(() => {
      this.handleOpenSidePanel.emit('LOYALTY_SIGN_IN');
    }, 550);
  };

  /**
   * Get error message for a specific field
   */
  public getErrorMsg(field: SignUpFormField, control: FormControl): boolean {
    if (!control?.touched) {
      return false;
    }

    return (
      control?.errors?.['required'] ||
      (field.invalidErrMsg && control?.invalid) ||
      (field.mismatchErrMsg && control?.errors?.['mismatch']) ||
      (field.minLengthErrMsg && control?.errors?.['minlength']) ||
      (field.maxLengthErrMsg && control?.errors?.['maxlength']) ||
      (field.patternErrMsg && control?.errors?.['pattern']) ||
      (field.requiredErrMsg && control?.errors?.['required'])
    );
  }

  /**
   * Check if form is US-based date format
   */
  public isUsBasedFormat = computed(() => {
    const dateFormat = this.propInfo().propertyDefaultDateFormat;
    return dateFormat?.toLowerCase().indexOf('mm') === 0;
  });

  /**
   * Handle checkbox selection changes
   */
  public handleCheckboxChange(event: any, fieldName: string): void {
    const isChecked = event.options?.[0]?.selected || false;
    this.registerFormGroup.get(fieldName)?.setValue(isChecked);
    this.registerFormGroup.get(fieldName)?.markAsTouched();
  }

  /**
   * Get checkbox data for a field
   */
  public getCheckboxData(field: SignUpFormField): CheckboxItem[] {
    return [
      {
        id: field.fieldName,
        label: '', // Empty label - we show text separately with link
        selected: this.registerFormGroup.get(field.fieldName)?.value || false,
      },
    ];
  }

  // TODO: Add password type from the API
  public passwordType = [
    {
      'passwordType': this.localeObj()?.tf_5_Loyalty_signup_password_uppercase,
    },
    {
      'passwordType': this.localeObj()?.tf_5_Loyalty_signup_password_lowercase,
    },
    {
      'passwordType': this.localeObj()?.tf_5_Loyalty_signup_password_number,
    },
    {
      'passwordType': this.localeObj()?.tf_5_Loyalty_signup_password_character,
    }
  ];

  /**
   * Handle sign-up form submission
   */
  public handleSignUpSubmit(): void {
    // Mark form as submitted
    this.isRegisterFormSubmitted.set(true);

    // Trim spaces from all text inputs and mark as touched
    Object.entries(this.registerFormGroup.controls).forEach(([key, control]) => {
      const value = control.value;

      // Trim string values
      if (typeof value === 'string' && SPACE_TAB_REGEX.test(value)) {
        control.patchValue(value.trim());
      }

      const field = this.registerFormFields.find((f) => f.fieldName === key);

      // Only validate if field is compulsory or has a value
      if (field?.compulsory || value) {
        control.markAsTouched({ onlySelf: true });
        control.updateValueAndValidity({ onlySelf: true });
      }
    });

    this.registerFormGroup.markAsTouched({ onlySelf: true });

    // Check if form is valid
    const isFormValid = this.registerFormGroup.valid || this.validateCompulsoryFields();

    if (isFormValid) {
      this.submitSignUpForm();
    } else {
      // Show toast message for form validation error
      this.modalSvc.displayToastMessage({
        desc: this.localeObj().tf_1_Calendar_Loyalty_signUp_fillAllFieldsErrMsg,
        type: 'error'
      });

      // Find first invalid field and focus on it
      const invalidField = this.registerFormFields.find((field) => {
        return this.registerFormGroup.controls[field.fieldName]?.invalid;
      });

      if (invalidField) {
        document.getElementById(invalidField.fieldName)?.focus();
        document.getElementById(invalidField.fieldName)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }

  /**
   * Validate that all compulsory fields are filled and valid
   */
  private validateCompulsoryFields(): boolean {
    const invalidCompulsoryFields = this.registerFormFields.filter((field) => {
      if (!field.compulsory) {
        return false;
      }

      // Check regular form controls (including phone numbers now)
      return (
        this.registerFormGroup.controls[field.fieldName]?.invalid &&
        !this.registerFormGroup.controls[field.fieldName]?.value
      );
    });

    return invalidCompulsoryFields.length === 0;
  }

  /**
   * Submit the sign-up form with reCAPTCHA verification
   */
  private submitSignUpForm(): void {
    if (checkRecaptchaConfig(this._storeSvc.propInfo())) {
      this.recaptchaSvc.executeAndVerifyRecaptcha({
        action: 'register',
        onSuccess: () => {
          this.doSubmitSignUpForm();
        },
        onError: () => {
          this.modalSvc.displayToastMessage({
            title: '',
            desc: this.localeObj().tf_4_googleRecaptcha_verificationFailed,
            type: 'error'
          });
        }
      });
    } else {
      this.doSubmitSignUpForm();
    }
  }

  /**
   * Actually submit the sign-up form (after reCAPTCHA verification)
   */
  private doSubmitSignUpForm(): void {
    const formData = { ...this.registerFormGroup.value };

    // Get property codes
    const propertyCodes = this.getPropertyCodes();

    // Process form data
    this.processFormData(formData);

    // Set loading state
    this.loyaltySvc.isSignUpInProgress.set(true);

    // Call sign-up API
    this.loyaltySvc.loyaltyRegister(formData, propertyCodes).subscribe({
      next: (response: any) => this.handleSignUpSuccess(response, propertyCodes),
      error: () => this.handleSignUpError(),
    });
  }

  /**
   * Process form data before submission
   */
  private processFormData(formData: any): void {
    // Combine country code from intl-tel-input with phone number from textbox
    if (formData.phone && this.phoneFormControl.value) {
      const dialCode = (this.phoneFormControl.value as any)?.dialCode || '';
      formData.phone = dialCode + formData.phone;
    }

    if (formData.business_phone && this.businessPhoneFormControl.value) {
      const dialCode = (this.businessPhoneFormControl.value as any)?.dialCode || '';
      formData.businessPhoneNumber = dialCode + formData.business_phone;
      delete formData.business_phone;
    }

    if (formData.cellPhone && this.cellPhoneFormControl.value) {
      const dialCode = (this.cellPhoneFormControl.value as any)?.dialCode || '';
      formData.cellPhone = dialCode + formData.cellPhone;
    }

    // Country and state are already stored as codes from dropdown selection
    // No conversion needed for country/state

    // Format date of birth
    if (formData.date_of_birth) {
      const dob = DateTime.fromObject({
        year: formData.date_of_birth.year,
        month: formData.date_of_birth.month,
        day: formData.date_of_birth.day,
      });
      formData.date_of_birth = dob.toFormat('yyyy-MM-dd');
    }

    // Remove confirmPassword field
    if (formData.confirmPassword) {
      delete formData.confirmPassword;
    }

    // Encrypt password if password signup is enabled
    const propInfo = this.propInfo();
    if (propInfo.loyaltySignUpSettingsInfo?.enable_password_signup && formData.password) {
      const isExtLoyaltyEnabled = !(propInfo.loyaltyProviderCode === 'GP');
      formData.password = isExtLoyaltyEnabled ? formData.password : Md5.init(formData.password);
    }

    // Remove null/empty values for non-required fields
    Object.keys(formData).forEach((key) => {
      let field = this.registerFormFields.find((f) => f.fieldName === key);

      // Handle mapped field names
      if (key === 'businessPhoneNumber') {
        field = this.registerFormFields.find((f) => f.fieldName === 'business_phone');
      }

      const value = formData[key];
      const isEmpty = value === null || value === undefined || value === '';

      if (isEmpty && !field?.compulsory) {
        delete formData[key];
      }
    });
  }

  /**
   * Handle sign-up success
   */
  private handleSignUpSuccess(response: any, propertyCodes: string): void {
    if (!!response && _.get(response, 'status.status_code') === 1000 && _.get(response, 'data.authorized')) {
      const accountId = _.get(response, 'data.accountId');

      // Get player info after successful sign-up
      this.getPlayerInfoAfterSignUp(accountId, propertyCodes);
    } else {
      // Handle sign-up error
      const errorCode = _.get(response, 'status.status_code');
      const errorMsg = this.localeObj()[`tf_1_Calendar_ExtLoyalty_signUp_errorCode_${errorCode}`] ||
        this.localeObj().tf_1_Calendar_ExtLoyalty_signUp_errorCode_general;

      this.modalSvc.displayToastMessage({ desc: errorMsg, type: 'error' });
      this.loyaltySvc.isSignUpInProgress.set(false);
    }
  }

  /**
   * Get player info after successful sign-up
   */
  private getPlayerInfoAfterSignUp(accountId: string, propertyCodes: string): void {
    const params = {
      accountId,
      loyaltyProviderCode: this.propInfo().loyaltyProviderCode,
      propertyCodes,
      playerTokenId: '',
    };

    this.loyaltySvc.getExtLoyaltyPlayerInfo(params).subscribe({
      next: (resp: any) => {
        if (!!resp && _.get(resp, 'status.status_code') === 1000) {
          // Check for responsible gaming exclusion
          if (
            this.propInfo().singlePropertyPortal &&
            _.get(resp, 'data[0].loyaltyProfile.propertyLoyaltyInfo[0].responsibleGaming') === 'Excluded'
          ) {
            const errorMsg = this.localeObj().tf_1_Calendar_ExtLoyalty_signIn_errorCode_1016;
            this.modalSvc.displayToastMessage({ desc: errorMsg, type: 'error' });
            this.loyaltySvc.isSignUpInProgress.set(false);
            return;
          }

          // Persist member profile; always write a fresh timestamp for new sign-ups
          this.loyaltySvc.buildAndStoreMemberProfile(resp.data[0], false, true);

          // Clear form and loading state
          this.clearForm();
          this.loyaltySvc.isSignUpInProgress.set(false);

          // post signup actions
          this.handlePostSignUpActions();

          // Show success modal if password signup is not enabled (passwordless flow)
          if (!this.propInfo().loyaltySignUpSettingsInfo?.enable_password_signup) {
            this.handleCloseSidePanel.emit();
            this.loyaltySvc.showSignUpSuccessModal.set(true);
          } else {
            this.handleCloseSidePanel.emit();
          }

          // Start session timeout monitoring
          this.sessionManagerSvc.startSessionTimeout();
        } else {
          const errorCode = _.get(resp, 'status.status_code');
          const errorMsg = this.localeObj()[`tf_1_Calendar_ExtLoyalty_signUp_errorCode_${errorCode}`] ||
            'An error occurred. Please try again.';

          this.modalSvc.displayToastMessage({ desc: errorMsg, type: 'error' });
          this.loyaltySvc.isSignUpInProgress.set(false);
        }
      },
      error: () => this.handleSignUpError(),
    });
  }

  /**
   * Handle sign-up error
   */
  private handleSignUpError(): void {
    this.modalSvc.displayToastMessage({
      desc: this.localeObj().tf_1_Calendar_ExtLoyalty_signUp_errorCode_general,
      type: 'error'
    });
    this.loyaltySvc.isSignUpInProgress.set(false);
  }

  /**
   * Handle post sign-up actions
   */
  private handlePostSignUpActions(): void {
    this.loyaltySvc.handlePostAuthActions();
  }

  /**
   * Clear the sign-up form
   */
  private clearForm(): void {
    this.registerFormGroup.reset();
    this.isRegisterFormSubmitted.set(false);

    // Reset phone number form controls
    this.phoneFormControl.reset();
    this.businessPhoneFormControl.reset();
    this.cellPhoneFormControl.reset();

    // Reset country to client country code
    const clientCountryName = this.clientCountry();
    const countryList = this.countryDataList();
    const clientCountryData = countryList.find(
      (c: any) => c.name?.toUpperCase() === clientCountryName?.toUpperCase()
    );
    const clientCountryCode = clientCountryData?.countryCode_2 || '';

    this.registerFormGroup.patchValue({ country: clientCountryCode });
    this.updateStatesDataList(clientCountryCode);
  }

  /**
   * Get property codes based on property configuration
   */
  private getPropertyCodes(): string {
    const propInfo = this.propInfo();
    const queryParams = this.route.snapshot.queryParams;

    // Single property or hotel query param present
    if (propInfo.singlePropertyPortal || queryParams[QUERY_PARAM_ATTRIBUTES.HOTEL]) {
      return propInfo.propertyCode;
    }

    // Multi-property - return property code (TODO: implement multi-property logic if needed)
    return propInfo.propertyCode;
  }
}
